import * as React from 'react';
import * as styled from './styled';
import {v4 as uuid} from 'uuid';
import {useFormikContext} from 'formik';
import {useBlockContext} from '../../hooks/useBlockContext';
import {BlockType, TaskObject} from 'api/task/types';
import DragControl from 'components/DragControl';
import ControlButton from 'components/ControlButton';
import {IconCopy, IconTrash} from 'icons';
import {HeaderProps} from './types';

/**
 * The header of a block.
 * @param {HeaderProps} props
 * @return {React.ReactElement<HeaderProps>}
 * @constructor
 */
export default function Header(
    props: HeaderProps,
): React.ReactElement<HeaderProps> {
  const {
    values, setFieldValue,
  } = useFormikContext<Partial<TaskObject>>();
  const blocks = values.content?.blocks || [];
  const block = useBlockContext();

  /**
   * Render drag control.
   * @return {JSX.Element}
   */
  function renderDragControl(): JSX.Element {
    return (
      <DragControl
        provided={props.provided}
        snapshot={props.snapshot}
      />
    );
  }

  /**
   * Render container.
   * @return {JSX.Element}
   */
  function renderContainer(): JSX.Element {
    /**
     * Render question.
     * @return {JSX.Element}
     */
    function renderQuestion(): JSX.Element {
      /**
       * The handle change event handler.
       * @param {React.ChangeEvent<HTMLTextAreaElement>} event
       */
      function handleChange(
        event: React.ChangeEvent<HTMLTextAreaElement>,
      ): void {
        setFieldValue(
          `content.blocks[${block.index}].question`,
          event.target.value,
        );
      }

      return (
        <styled.Question
          value={block.question}
          placeholder="Введите вопрос"
          onChange={handleChange}
          required
        />
      );
    }

    /**
     * Render description.
     * @return {JSX.Element | undefined}
     */
    function renderDescription(): JSX.Element | undefined {
      /**
       * The handle change event handler.
       * @param {React.ChangeEvent<HTMLTextAreaElement>} event
       */
      function handleChange(
        event: React.ChangeEvent<HTMLTextAreaElement>,
      ): void {
        setFieldValue(
          `content.blocks[${block.index}].description`,
          event.target.value,
        );
      }

      return block.type !== BlockType.FreeAnswer ? (
        <styled.Description
          value={block.description}
          placeholder="Введите описание"
          onChange={handleChange}
        />
      ) : undefined;
    }

    return (
      <styled.Container>
        {renderQuestion()}
        {renderDescription()}
      </styled.Container>
    );
  }

  /**
   * Render controls.
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    /**
     * Render copy.
     * @return {JSX.Element}
     */
    function renderCopy(): JSX.Element {
      /**
       * The handle change event handler.
       */
      function handleClick( ): void {
        blocks.splice(block.index, 0, {
          ...block,
          id: uuid(),
        });
        setFieldValue(
          `content.blocks`,
          blocks,
        );
      }

      return (
        <ControlButton type="button" onClick={handleClick}>
          <IconCopy />
        </ControlButton>
      );
    }

    /**
     * Render delete.
     * @return {JSX.Element}
     */
    function renderDelete(): JSX.Element {
      /**
       * The handle change event handler.
       */
      function handleClick( ): void {
        setFieldValue(
          `content.blocks`,
          blocks.filter((_block) => (
            _block.id !== block.id
          )),
        );
      }

      return (
        <ControlButton type="button" onClick={handleClick}>
          <IconTrash />
        </ControlButton>
      );
    }

    return (
      <styled.Controls>
        {renderCopy()}
        {renderDelete()}
      </styled.Controls>
    );
  }

  return (
    <styled.Wrapper>
      {renderDragControl()}
      {renderContainer()}
      {renderControls()}
    </styled.Wrapper>
  );
}
